import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import { Modal } from 'flowbite';

import ExpenseDepartmentRow from '../cashflow_central_module/components/expense/ExpenseDepartmentRow';
import SectionHeader from '../cashflow_central_module/components/shared/SectionHeader';

import { modalOptions } from 'Helpers/modalOptions';

import LoadingOverlay from 'CommonComponents/LoadingOverlay';
import SimpleConfirmationModal from 'CommonComponents/SimpleConfirmationModal';

export default function DepartmentCashflowModuleTable(props) {
    const [maxMilestonesCount, setMaxMilestonesCount] = useState([]);
    const [milestonePeriods, setMilestonePeriods] = useState([]);
    const [editingExpenseId, setEditingExpenseId] = useState(null);
    const [editingType, setEditingType] = useState(null);
    const [editingMilestoneId, setEditingMilestoneId] = useState(null);
    const [modalTitle, setModalTitle] = useState("Milestone couldn't be updated");
    const [modalText, setModalText] = useState('Error updating Milestone values');
    const [cashflowCentralModuleAlertModalElement, setCashflowCentralModuleAlertModalElement] = useState(null);

    const [expenditures, setExpenditures] = useState([]);
    const [showExpenditures, setShowExpenditures] = useState(true);
    const [loading, setLoading] = useState(true);
    const [firstLoading, setFirstLoading] = useState(true);

    const cashflowCentralModuleAlertModalRef = useRef();

    useEffect(() => {
        cashflowCentralModuleAlertModalRef &&
            setCashflowCentralModuleAlertModalElement(new Modal(cashflowCentralModuleAlertModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (props.refreshCashflowCount > 0) {
            fetchCentralCashflow(props.refreshCashflowCount === 1);
        }
    }, [props.refreshCashflowCount]);

    const fetchCentralCashflow = (isFirstTimeLoading = false) => {
        setLoading(true);
        if (isFirstTimeLoading) {
            setFirstLoading(true);
        }
        axios
            .get(props.CashflowModuleIndex, {
                params: {
                    selectedMonths: props.selectedMonths,
                    displayBudgetLines: props.displayBudgetLines,
                },
            })
            .then((res) => {
                setMilestonePeriods(res.data.periods);
                setMaxMilestonesCount(res.data.maxExpenseMilestonesCount);
                setExpenditures(res.data.expenditureData.Expenditure);
                setLoading(false);
                if (isFirstTimeLoading) {
                    setFirstLoading(false);
                }
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
                if (isFirstTimeLoading) {
                    setFirstLoading(false);
                }
            });
    };

    const increaseMaxMilestonesCount = useCallback(() => {
        setMaxMilestonesCount((prev) => prev + 1);
    }, []);

    const setExpenseEditing = useCallback((expense_id, milestone_id, type) => {
        setEditingExpenseId(expense_id);
        setEditingType(type);
        setEditingMilestoneId(milestone_id);
    }, []);

    const modalShow = useCallback(
        (showModal, title = '', text = '') => {
            setModalTitle(title);
            setModalText(text);
            if (showModal) {
                cashflowCentralModuleAlertModalElement && cashflowCentralModuleAlertModalElement.show();
            } else {
                cashflowCentralModuleAlertModalElement && cashflowCentralModuleAlertModalElement.hide();
            }
        },
        [cashflowCentralModuleAlertModalElement],
    );

    // Toggle functions optimized with useCallback
    const toggleExpenditures = useCallback(() => setShowExpenditures(!showExpenditures), [showExpenditures]);

    // Handle expand all trigger from parent with staggered updates
    useEffect(() => {
        const t = props.expandAllTrigger;
        if (!t?.version) return;

        // Only update sections when it's the sections batch or no batch specified (fallback)
        if (!t.batch || t.batch === 'sections') {
            setShowExpenditures(t.expanded);
        }
    }, [props.expandAllTrigger?.version]);

    // Memoized common props to prevent unnecessary re-renders
    const commonProps = useMemo(
        () => ({
            minDate: props.minDate,
            maxDate: props.maxDate,
            modalShow,
            fetchCentralCashflow,
            canEdit: props.canEdit,
            periods: milestonePeriods,
            expandAllTrigger: props.expandAllTrigger,
            editAllTrigger: props.editAllTrigger,
        }),
        [props.minDate, props.maxDate, props.canEdit, props.expandAllTrigger, props.editAllTrigger, milestonePeriods, modalShow],
    );

    const expenseProps = useMemo(
        () => ({
            ...commonProps,
            ExpenseMilestoneUpdateUrl: props.ExpenseMilestoneUpdateUrl,
            maxExpenseMilestonesCount: maxMilestonesCount,
            increaseMaxExpenseMilestonesCount: increaseMaxMilestonesCount,
            editingExpenseId,
            editingType,
            editingMilestoneId,
            setExpenseEditing: setExpenseEditing,
            setLoading: setLoading,
            costcentersColor: true,
            milestoneType: 'expenditure',
            indentLevel: 0,
        }),
        [
            commonProps,
            props.ExpenseMilestoneUpdateUrl,
            maxMilestonesCount,
            increaseMaxMilestonesCount,
            editingExpenseId,
            editingType,
            editingMilestoneId,
            setExpenseEditing,
        ],
    );

    return (
        <>
            <LoadingOverlay isVisible={loading} />
            <div className="inbud-table_wrapper">
                <table className="inbud-table inbud-table_first-two-sticky-80">
                    <thead className="tw-bg-blue-25">
                        <tr className="*:tw-font-extrabold *:!tw-pb-1.5">
                            <th></th>
                            <th></th>
                            <th className="tw-border-r-2"></th>
                            {milestonePeriods.map((milestonePeriod, i) => (
                                <th key={`th_milestones_${i}`} colSpan={2} className="tw-uppercase tw-border-r-2">
                                    {milestonePeriod}
                                </th>
                            ))}
                            <th colSpan={2} className="tw-border-r-2">
                                {$t('Total')}
                            </th>
                            <th></th>
                        </tr>
                        <tr className="*:!tw-pt-1.5">
                            <th className="tw-min-w-80">
                                {$t('Department')} / {$t('Cost Centre')} / {$t('Budget Line')}
                            </th>
                            <th className="tw-min-w-20">{$t('Type')}</th>
                            <th className="tw-min-w-28 tw-border-r-2">{$t('Value')}</th>
                            {milestonePeriods.map((milestonePeriod, i) => (
                                <React.Fragment key={`th_milestones_${i}_date_percentage`}>
                                    <th className="tw-min-w-16">%</th>
                                    <th className="tw-min-w-32 tw-border-r-2">{$t('Value')}</th>
                                </React.Fragment>
                            ))}
                            <th className="tw-min-w-16">%</th>
                            <th className="tw-min-w-32 tw-border-r-2">{$t('Value')}</th>
                            <th className="tw-min-w-32">{$t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && firstLoading ? (
                            <tr>
                                <td colSpan="11">{$t('Loading')}...</td>
                            </tr>
                        ) : (
                            <>
                                <SectionHeader
                                    title={$t('Expenditure')}
                                    isExpanded={showExpenditures}
                                    onToggle={toggleExpenditures}
                                    maxMilestonesCount={maxMilestonesCount}
                                />
                                {showExpenditures &&
                                    expenditures.length > 0 &&
                                    expenditures.map((department) => (
                                        <ExpenseDepartmentRow
                                            {...expenseProps}
                                            key={`tr_department_${department.id}`}
                                            department={department}
                                            fetchCentralCashflow={fetchCentralCashflow}
                                        />
                                    ))}
                                {showExpenditures && expenditures.length === 0 && (
                                    <tr>
                                        <td colSpan={maxMilestonesCount * 2 + 5}>
                                            {$t('No {{type}} Found!', {
                                                type: 'Expenditure Departments',
                                            })}
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
                <SimpleConfirmationModal
                    closeHandler={() => modalShow(false)}
                    allowAction={true}
                    modalId={'cashflow-central-module-alert'}
                    modalRef={cashflowCentralModuleAlertModalRef}
                    modalElement={cashflowCentralModuleAlertModalElement}
                    confirmationMsg={modalText}
                    title={modalTitle}
                    showSubmitButton={false}
                    cancelButtonText={$t('Close')}
                />
            </div>
        </>
    );
}
